/**
 * DeleteTenantDialog.jsx
 *
 * Type-the-name-to-confirm dialog for permanent tenant deletion. The
 * confirm button stays disabled until the typed value matches the
 * tenant's actual name — this is the standard safety pattern for
 * destructive actions (GitHub, Stripe, Linear all use it).
 *
 * The actual delete is irreversible — the backend cascades through
 * users, campaigns, decisions, share_links, tenant_features and the
 * tenant doc itself. See adminDeleteTenant in functions/index.js.
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebaseConfig";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDarkMode } from "../../hooks/useDarkMode";
import { FiX, FiAlertTriangle, FiRefreshCw, FiTrash2 } from "react-icons/fi";

export default function DeleteTenantDialog({ open, tenant, onClose, onDeleted }) {
  const { t, isRTL } = useLanguage();
  const { darkMode } = useDarkMode();

  const [typed, setTyped]   = useState("");
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");

  // Reset state every time the dialog opens. Prevents the previous
  // typed value or error from leaking into a new delete attempt.
  useEffect(() => {
    if (!open) return;
    setTyped("");
    setError("");
  }, [open, tenant?.id]);

  const targetName = tenant?.name || tenant?.companyName || "";
  const canDelete  = typed.trim() === targetName.trim() && targetName.length > 0;

  if (!open || !tenant) return null;

  const handleDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    setError("");
    try {
      const fn = httpsCallable(functions, "adminDeleteTenant");
      await fn({ tenantId: tenant.id, confirm: true });
      onDeleted?.(tenant.id);
      onClose?.();
    } catch (err) {
      setError(err.message || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const cardCls = darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const inputCls = `w-full border rounded-xl px-4 py-2.5 outline-none text-sm transition-colors ${
    darkMode
      ? "bg-gray-900 border-gray-700 text-white focus:border-red-500"
      : "bg-gray-50 border-gray-300 text-gray-900 focus:border-red-500"
  }`;
  const headingCls = darkMode ? "text-white" : "text-gray-900";
  const subCls = darkMode ? "text-gray-400" : "text-gray-600";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          dir={isRTL ? "rtl" : "ltr"}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            className={`${cardCls} border rounded-2xl shadow-2xl w-full max-w-md`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-700/40">
              <h2 className={`text-lg font-semibold flex items-center gap-2 text-red-400`}>
                <FiAlertTriangle /> {t("admin_delete_tenant")}
              </h2>
              <button onClick={onClose} type="button"
                className={`p-2 rounded-xl ${darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}>
                <FiX />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className={`${subCls} text-sm`}>{t("admin_delete_warning")}</p>

              <div className={`rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm`}>
                <p className={headingCls}>
                  {/* The name is rendered as plain text (not inside an
                      input) so it can't be selected and copy-pasted —
                      the user has to type it, which is the whole point
                      of the confirmation pattern. */}
                  <span className="font-mono font-semibold">{targetName}</span>
                </p>
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1.5 ${subCls}`}>
                  {t("admin_delete_confirm_label")}
                </label>
                <input
                  type="text"
                  value={typed}
                  onChange={e => setTyped(e.target.value)}
                  className={inputCls}
                  autoFocus
                  // Prevent accidental autocomplete suggestions from
                  // satisfying the confirmation requirement.
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>

              {error && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-700/40">
                <button
                  type="button"
                  onClick={onClose}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${
                    darkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {t("admin_cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={!canDelete || busy}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${
                    !canDelete || busy
                      ? "bg-gray-500/20 text-gray-400 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700 text-white"
                  }`}
                >
                  {busy ? <FiRefreshCw className="animate-spin" /> : <FiTrash2 />}
                  {t("admin_delete_tenant")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
